import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";

import { sort } from "@/lib/sort";
import { novaClient } from "@/lib/trpc";
import { EducationType } from "@/types";

// All education CRUD in one hook. Writes change the profile served by the
// sheet-data query, so each mutation updates that cache in place at the hook
// level — every sheet view stays in sync even when the calling editor
// unmounts before the mutation resolves.
export function useEducation() {
  const queryClient = useQueryClient();

  const setSheetDataEducations = (
    userId: number,
    updater: (educations: EducationType[]) => EducationType[],
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const [, params] = query.queryKey as [
            unknown,
            { input?: { userId?: number } }?,
          ];
          return params?.input?.userId === userId;
        },
        queryKey: getQueryKey(
          novaClient.internal.user.active.getCandidacySheetData,
        ),
      },
      (cached: unknown) => {
        if (!cached) return cached;
        const data = cached as { education?: EducationType[] };
        // Re-sort so a created or re-dated row lands where the server would
        // have put it instead of at the end of the list.
        return {
          ...data,
          education: sort.sortEducations([...updater(data.education ?? [])]),
        };
      },
    );
  };

  const createEducation =
    novaClient.internal.user.profile.education.create.useMutation({
      onSuccess: (created, variables) => {
        setSheetDataEducations(variables.userId, (educations) => [
          ...educations,
          created as EducationType,
        ]);
      },
    });

  const updateEducation =
    novaClient.internal.user.profile.education.update.useMutation({
      onSuccess: (updated, variables) => {
        const updatedEducation = updated as EducationType;
        setSheetDataEducations(variables.userId, (educations) =>
          educations.map((education) =>
            education.id === updatedEducation.id
              ? { ...education, ...updatedEducation }
              : education,
          ),
        );
      },
    });

  const deleteEducation =
    novaClient.internal.user.profile.education.delete.useMutation({
      onSuccess: (_deleted, variables) => {
        setSheetDataEducations(variables.userId, (educations) =>
          educations.filter((education) => education.id !== variables.id),
        );
      },
    });

  return { createEducation, deleteEducation, updateEducation };
}
